import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import {
  IconCopy,
  IconDots,
  IconExternalLink,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type CSSProperties, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { MessageKey } from "../../shared/i18n";
import { getTemplate, TEMPLATES } from "../../shared/templates";
import { endpoints, type ResumeSummary } from "../api/client";
import { resumesQuery, useDeleteResume, useNotifyError } from "../api/queries";
import { useAuth } from "../auth/provider";
import { AppFrame } from "../components/app-frame";
import { ConfirmDialog } from "../components/confirm-dialog";
import { EmptyState } from "../components/empty-state";
import { Page, PageHeader } from "../components/page";
import { formatRelative } from "../i18n/format";
import { useLocale, useMessages } from "../i18n/provider";
import styles from "./dashboard.module.css";

const UPGRADE_DISMISSED = "yc_upgrade_dismissed";

export function DashboardPage() {
  const t = useMessages();
  const { locale } = useLocale();
  const { me } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notifyError = useNotifyError();
  const deleteResume = useDeleteResume();

  const { data, isLoading } = useQuery(resumesQuery);
  const items = data?.items ?? [];

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState("");
  const [createTemplate, setCreateTemplate] = useState("minimal");
  const [createLocale, setCreateLocale] = useState(locale);
  const [renaming, setRenaming] = useState<ResumeSummary | null>(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [deleting, setDeleting] = useState<ResumeSummary | null>(null);
  const [upgradeDismissed, setUpgradeDismissed] = useState(
    () => localStorage.getItem(UPGRADE_DISMISSED) === "1",
  );

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) =>
      [item.title, item.fullName, item.headline ?? ""].join(" ").toLowerCase().includes(query),
    );
  }, [items, search]);

  const create = useMutation({
    mutationFn: () =>
      endpoints.createResume({
        title: createTitle.trim(),
        templateId: createTemplate,
        locale: createLocale,
      }),
    onSuccess: async (summary) => {
      await queryClient.invalidateQueries({ queryKey: ["resumes"] });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate(`/resumes/${summary.id}/edit`);
    },
    onError: notifyError,
  });

  const duplicate = useMutation({
    mutationFn: (id: string) => endpoints.duplicateResume(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["resumes"] });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: notifyError,
  });

  const rename = useMutation({
    mutationFn: () => {
      if (!renaming) throw new Error("No resume selected");
      return endpoints.updateResume(renaming.id, { title: renameTitle.trim() });
    },
    onSuccess: async () => {
      setRenaming(null);
      await queryClient.invalidateQueries({ queryKey: ["resumes"] });
      await queryClient.invalidateQueries({ queryKey: ["resume", renaming?.id] });
    },
    onError: notifyError,
  });

  const templateChoices = useMemo(() => {
    const allowed =
      me?.limits.allTemplates === true
        ? TEMPLATES
        : TEMPLATES.filter((meta) => meta.tier === "free");
    return allowed.map((meta) => ({
      value: meta.id,
      label: t(meta.nameKey as MessageKey),
    }));
  }, [me?.limits.allTemplates, t]);

  const limit = me?.limits.resumes ?? 2;
  const used = me?.usage.resumes ?? items.length;
  const atLimit = used >= limit;

  const openCreate = () => {
    setCreateTitle("");
    setCreateTemplate("minimal");
    setCreateLocale(locale);
    setCreateOpen(true);
  };

  const dismissUpgrade = () => {
    localStorage.setItem(UPGRADE_DISMISSED, "1");
    setUpgradeDismissed(true);
  };

  return (
    <AppFrame active="/dashboard">
      <Page>
        <PageHeader
          title={
            <Group gap={10} align="baseline">
              {t("dashboard.title")}
              <Text size="sm" c="dimmed" fw={400}>
                {t("dashboard.count", { count: items.length })}
              </Text>
            </Group>
          }
          action={
            <Button leftSection={<IconPlus size={16} stroke={2} />} onClick={openCreate}>
              {t("dashboard.newCv")}
            </Button>
          }
        />

        {me ? (
          <div className={styles.quota}>
            <Text size="sm" c="dimmed">
              {t("dashboard.quota", { used, limit })}
            </Text>
            {me.plan === "free" ? (
              <Link to="/billing" className={styles.quotaLink}>
                {t("common.upgrade")}
              </Link>
            ) : null}
          </div>
        ) : null}

        {items.length >= 8 ? (
          <TextInput
            className={styles.search}
            placeholder={t("dashboard.searchPlaceholder")}
            leftSection={<IconSearch size={16} stroke={1.5} />}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            mb="lg"
          />
        ) : null}

        {me?.plan === "free" && items.length > 0 && !upgradeDismissed ? (
          <div className={styles.upgrade}>
            <div>
              <Text fw={600} size="sm">
                {t("dashboard.upgradeTitle")}
              </Text>
              <Text size="sm" c="dimmed">
                {t("dashboard.upgradeBody")}
              </Text>
            </div>
            <Group gap="sm">
              <Button variant="light" size="xs" component={Link} to="/billing">
                {t("common.upgrade")}
              </Button>
              <Button variant="subtle" color="gray" size="xs" onClick={dismissUpgrade}>
                {t("common.dismiss")}
              </Button>
            </Group>
          </div>
        ) : null}

        {isLoading ? (
          <div className={styles.skeletonGrid} aria-busy="true">
            {[0, 1, 2].map((index) => (
              <div key={index} className={styles.skeletonCard} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={t("dashboard.emptyTitle")}
            body={t("dashboard.emptyBody")}
            action={
              <Button onClick={openCreate} size="sm">
                {t("dashboard.newCv")}
              </Button>
            }
          />
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
            {visible.map((item) => (
              <ResumeCard
                key={item.id}
                summary={item}
                onRename={() => {
                  setRenaming(item);
                  setRenameTitle(item.title);
                }}
                onDelete={() => setDeleting(item)}
                onDuplicate={() => duplicate.mutate(item.id)}
              />
            ))}
          </SimpleGrid>
        )}

        {items.length > 0 && visible.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" mt="xl">
            {t("errors.notFound")}
          </Text>
        ) : null}
      </Page>

      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("dashboard.newCv")}
        size="sm"
      >
        <Stack>
          <TextInput
            label={t("common.rename")}
            placeholder={t("dashboard.untitled")}
            value={createTitle}
            onChange={(event) => setCreateTitle(event.currentTarget.value)}
            data-autofocus
            maxLength={120}
          />
          <Group grow>
            <Select
              label={t("nav.templates")}
              data={templateChoices}
              value={createTemplate}
              onChange={(value) => setCreateTemplate(value ?? "minimal")}
              allowDeselect={false}
            />
            <Select
              label={t("common.language")}
              data={[
                { value: "en", label: t("common.english") },
                { value: "vi", label: t("common.vietnamese") },
              ]}
              value={createLocale}
              onChange={(value) => setCreateLocale(value === "vi" ? "vi" : "en")}
              allowDeselect={false}
            />
          </Group>
          {atLimit ? (
            <Text size="sm" c="dimmed">
              {t("errors.resumeLimit")}{" "}
              <Link to="/billing" className={styles.inlineLink}>
                {t("common.upgrade")}
              </Link>
            </Text>
          ) : null}
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={!createTitle.trim() || atLimit}
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              {t("common.create")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={Boolean(renaming)}
        onClose={() => setRenaming(null)}
        title={t("common.rename")}
        size="sm"
      >
        <Stack>
          <TextInput
            value={renameTitle}
            onChange={(event) => setRenameTitle(event.currentTarget.value)}
            maxLength={120}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="subtle" color="gray" onClick={() => setRenaming(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={!renameTitle.trim()}
              loading={rename.isPending}
              onClick={() => rename.mutate()}
            >
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ConfirmDialog
        opened={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title={t("common.delete")}
        body={t("dashboard.deleteConfirm")}
        confirmLabel={t("common.delete")}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await deleteResume.mutateAsync(deleting.id);
          } catch (error) {
            notifyError(error);
          }
        }}
      />
    </AppFrame>
  );
}

function ResumeCard({
  summary,
  onRename,
  onDelete,
  onDuplicate,
}: {
  summary: ResumeSummary;
  onRename: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const t = useMessages();
  const { locale } = useLocale();
  const template = getTemplate(summary.templateId);
  const templateName = template ? t(template.nameKey as MessageKey) : summary.templateId;
  const name = summary.fullName.trim() || summary.title;

  return (
    <article className={styles.card}>
      <Link to={`/resumes/${summary.id}/edit`} className={styles.thumbLink}>
        <span
          className={styles.thumb}
          style={{ "--card-accent": summary.accent } as CSSProperties}
          aria-hidden="true"
        >
          <span className={styles.thumbAccent} />
          <span className={styles.thumbName}>{name}</span>
          {summary.headline ? (
            <span className={styles.thumbHeadline}>{summary.headline}</span>
          ) : null}
          <span className={styles.thumbRule} />
          <span className={styles.thumbLines} />
          <span className={styles.thumbLinesShort} />
          <span className={styles.thumbFooter}>{templateName}</span>
        </span>
      </Link>
      <div className={styles.cardBody}>
        <div className={styles.cardText}>
          <Link to={`/resumes/${summary.id}/edit`} className={styles.cardTitle}>
            {summary.title || t("dashboard.untitled")}
          </Link>
          <Text size="xs" c="dimmed">
            {t("dashboard.edited", {
              when: formatRelative(summary.updatedAt, locale),
            })}
          </Text>
        </div>
        <Group gap={6}>
          {summary.visibility !== "private" ? (
            <Badge size="xs" variant="light" color="gray">
              {t("share.published")}
            </Badge>
          ) : null}
          <Menu position="bottom-end" width={190} shadow="md">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" aria-label={t("common.moreOptions")}>
                <IconDots size={18} stroke={1.5} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                component={Link}
                to={`/resumes/${summary.id}/edit`}
                leftSection={<IconPencil size={15} stroke={1.5} />}
              >
                {t("common.edit")}
              </Menu.Item>
              {summary.visibility !== "private" ? (
                <Menu.Item
                  component="a"
                  href={`/r/${summary.slug}`}
                  target="_blank"
                  leftSection={<IconExternalLink size={15} stroke={1.5} />}
                >
                  {t("common.open")}
                </Menu.Item>
              ) : null}
              <Menu.Item onClick={onDuplicate} leftSection={<IconCopy size={15} stroke={1.5} />}>
                {t("common.duplicate")}
              </Menu.Item>
              <Menu.Item onClick={onRename} leftSection={<IconPencil size={15} stroke={1.5} />}>
                {t("common.rename")}
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                color="red"
                onClick={onDelete}
                leftSection={<IconTrash size={15} stroke={1.5} />}
              >
                {t("common.delete")}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </div>
    </article>
  );
}
